import { Component, inject, OnInit, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { DashboardService } from '../../services/dashboard/dashboard.service';
import { catchError, takeUntil } from 'rxjs/operators';
import { of } from 'rxjs';
import { ConnectionSelfSignupStatus } from '../../../self-signup/models/connection-self-signup';
import { AuthService } from '../../../auth/services/auth.service';
import { PermissionCode } from '../../../../shared/enums/permission-code';
import { LeadsService } from '../../../leads/services/leads.service';

@Component({
  selector: 'app-dashboard-pending-actions',
  standalone: true,
  imports: [],
  templateUrl: './dashboard-pending-actions.component.html',
  styleUrl: './dashboard-pending-actions.component.scss',
})
export class DashboardPendingActionsComponent
  extends DestroyBase
  implements OnInit
{
  private dashboardService = inject(DashboardService);
  private router = inject(Router);
  private authService = inject(AuthService);
  private leadsService = inject(LeadsService);

  @Output() reviewLeadStatus = new EventEmitter<void>();

  pendingLeadAssignmentsCount = 0;
  pendingSignupRequestsCount = 0;
  pendingPdmApprovalCount = 0;
  isPendingActionsExpanded = true;
  isLoading = true;

  hasAdminPrivilege: boolean = this.authService.hasPermission(
    PermissionCode.ADMIN_PRIVILEGES,
  );

  get totalPendingActionsCount(): number {
    let count = this.pendingLeadAssignmentsCount || 0;
    if (this.hasAdminPrivilege) {
      count +=
        (this.pendingSignupRequestsCount || 0) +
        (this.pendingPdmApprovalCount || 0);
    }
    return count;
  }

  private readonly STORAGE_KEY = 'dashboard_expanded_sections';

  ngOnInit(): void {
    this.loadExpandedState();
    this.loadPendingActionsCount();

    // Subscribe to refresh trigger
    this.leadsService.statusCountRefresh$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadPendingActionsCount();
      });
  }

  private loadPendingActionsCount(): void {
    this.isLoading = true;
    this.dashboardService
      .getDashboardPendingActionsCount()
      .pipe(
        catchError(() =>
          of({
            leads_without_status: 0,
            org_signup_approvals: 0,
            pdm_approvals: 0,
          }),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((counts) => {
        this.pendingLeadAssignmentsCount = counts?.leads_without_status ?? 0;
        this.pendingSignupRequestsCount = counts?.org_signup_approvals ?? 0;
        this.pendingPdmApprovalCount = counts?.pdm_approvals ?? 0;
        this.isLoading = false;
      });
  }

  togglePendingActions(): void {
    this.isPendingActionsExpanded = !this.isPendingActionsExpanded;
    this.saveExpandedState();
  }

  private loadExpandedState(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored !== null) {
        const parsed = JSON.parse(stored);
        if (typeof parsed?.pendingActions === 'boolean') {
          this.isPendingActionsExpanded = parsed.pendingActions;
        }
      }
    } catch {
      this.isPendingActionsExpanded = true;
    }
  }

  private saveExpandedState(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : {};
      parsed.pendingActions = this.isPendingActionsExpanded;
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(parsed));
    } catch {
      // Ignore storage errors
    }
  }

  onReviewLeadStatus(): void {
    this.reviewLeadStatus.emit();
  }

  navigateToSignupRequests(): void {
    this.router.navigate(['/connection-signup-requests'], {
      queryParams: { status: ConnectionSelfSignupStatus.Pending },
    });
  }

  navigateToPdmRequests(): void {
    this.router.navigate(['/files'], {
      queryParams: { pdm_only: true, pdm_sharing_status: 2 },
    });
  }
}
